import { type HTMLAttributes } from 'react';
import { CurrencyDisplay } from '../CurrencyDisplay/CurrencyDisplay';
import { cn } from '../../utils/cn';

/**
 * Structurally identical to `CancellationPolicyTier` from `@bravemove/types`.
 * Declared locally so `@bravemove/ui-web` stays free of a cross-package types dep
 * — mirrors the `LifecycleChip` / `BookingStatusRibbon` precedent in this
 * package. Values from `@bravemove/types`'s `CancellationPolicyTier` are
 * assignable to this shape and vice versa.
 */
export type CancelTierTrackTier = {
  readonly hoursBefore: number;
  readonly refundPercent: number;
};

/** Structurally identical to `CancellationPolicy` from `@bravemove/types`. */
export type CancelTierTrackPolicy = {
  readonly tiers: readonly CancelTierTrackTier[];
};

/** Structurally identical to `Money` from `@bravemove/types`. */
export type CancelTierTrackMoney = {
  readonly amount: number;
  readonly currency: string;
};

/**
 * F17 — Cancellation policy refund tier visualiser.
 *
 * Renders the active venue's `CancellationPolicy` as a horizontal multi-band
 * time-axis track plus a side panel showing TIME TO START + YOUR REFUND.
 * The "you are here" marker is positioned by `hoursToStart / maxHours`.
 *
 * Spec source: `docs/design/screens/booking-lib.jsx` — `CancelTierTrack`.
 * F16-F18 design polish wave, Wave 1 1.6.
 *
 * Tier matching mirrors the server-side `CancellationPolicyService.computeRefund`
 * and the portal-local helper `apps/portal/src/lib/cancellation-policy-client.ts`:
 * find the FIRST tier (sorted strictly DESCENDING by `hoursBefore`) whose
 * `hoursBefore` threshold the value meets. The last tier MUST have
 * `hoursBefore = 0` so any value matches. Refund amount is
 * `floor(basePrice.amount × refundPercent / 100)` (rounded DOWN — matches the
 * server-side rule that avoids over-refund to a fraction of a cent).
 *
 * The helper is inlined deliberately: the canonical client mirror lives in
 * `apps/portal/src/lib/cancellation-policy-client.ts` but is NOT a workspace
 * package, so the primitive can't import it cleanly. The CLAUDE.md design
 * convention forbids duplicating money math AT THE CALL-SITE; in a shared
 * primitive a self-contained pure helper is the lesser evil and the test
 * suite asserts parity against the same matching rule.
 */
export interface CancelTierTrackProps extends HTMLAttributes<HTMLDivElement> {
  readonly policy: CancelTierTrackPolicy;
  /** Number of hours between now and slot start. Negative values clamp to 0. */
  readonly hoursToStart: number;
  /** Booking's snapshotted price. Refund amount derives from this × tier %. */
  readonly basePrice: CancelTierTrackMoney;
  /**
   * Right edge of the time axis in hours. Defaults to 48 — matches the
   * three-tier platform default policy. Increase if the policy declares any
   * threshold > 48 (e.g. a 72h tier) so the marker still fits inside the band.
   */
  readonly maxHours?: number;
  readonly locale?: string;
}

/** Map a tier index → semantic tone. Lowest-refund tier = danger, highest = success. */
type TierTone = 'danger' | 'warning' | 'success';

function toneForTierIndex(tierCount: number, indexFromLowToHigh: number): TierTone {
  // Bands are rendered low → high (left = least refund, right = most refund).
  // For a 3-tier policy: 0 → danger, 1 → warning, 2 → success.
  // For 2 tiers: 0 → danger, 1 → success. For >3 tiers the middle ones share warning.
  if (tierCount <= 1) return 'success';
  if (indexFromLowToHigh === 0) return 'danger';
  if (indexFromLowToHigh === tierCount - 1) return 'success';
  return 'warning';
}

const TONE_CLASSES: Record<
  TierTone,
  { bg: string; text: string; activeBorder: string; activeBg: string }
> = {
  danger: {
    bg: 'bg-danger/30',
    text: 'text-danger',
    activeBorder: 'border-danger',
    activeBg: 'bg-danger/45',
  },
  warning: {
    bg: 'bg-warning/30',
    text: 'text-warning',
    activeBorder: 'border-warning',
    activeBg: 'bg-warning/45',
  },
  success: {
    bg: 'bg-success/30',
    text: 'text-success',
    activeBorder: 'border-success',
    activeBg: 'bg-success/45',
  },
};

/**
 * Pure helper — match `hoursToStart` against the policy's tiers.
 * Mirrors `apps/portal/src/lib/cancellation-policy-client.ts::computeRefundClientSide`.
 *
 * Returns the matched tier's index INTO `policy.tiers` (which is sorted strictly
 * descending by `hoursBefore`) — index 0 = highest-refund tier.
 */
export function matchTierIndex(policy: CancelTierTrackPolicy, hoursToStart: number): number {
  const v = Math.max(0, hoursToStart);
  // Tiers are sorted strictly descending; the last tier (hoursBefore=0) always matches.
  for (let i = 0; i < policy.tiers.length; i++) {
    const tier = policy.tiers[i]!;
    if (v >= tier.hoursBefore) {
      return i;
    }
  }
  return policy.tiers.length - 1;
}

/** Format `hoursToStart` (decimal) as "{H}H {M}M". */
function formatTimeToStart(hoursToStart: number): string {
  const clamped = Math.max(0, hoursToStart);
  const h = Math.floor(clamped);
  const m = Math.round((clamped - h) * 60);
  // Cap rounding overflow: 0.999h → 1H 0M, not 0H 60M.
  if (m === 60) return `${h + 1}H 0M`;
  return `${h}H ${m}M`;
}

/**
 * Build the ordered band list (LEFT → RIGHT == low refund → high refund).
 *
 * For each band we know:
 * - the tier's index INTO `policy.tiers` (descending-by-hoursBefore order)
 * - the band's width in HOURS (right-edge − left-edge on the time axis)
 * - the tier's `hoursBefore` (= the band's LEFT edge in hours)
 * - the tier's `refundPercent`
 * - the band's tone (danger / warning / success)
 * - the band's right edge label ("<6H", "6-24H", ">24H" for the default policy)
 */
type Band = {
  readonly tierIndex: number; // index into policy.tiers
  readonly leftHours: number;
  readonly widthHours: number;
  readonly refundPercent: number;
  readonly tone: TierTone;
  readonly label: string;
};

function buildBands(policy: CancelTierTrackPolicy, maxHours: number): readonly Band[] {
  // policy.tiers is strictly DESC by hoursBefore; reverse to get low → high.
  const lowToHigh = policy.tiers.slice().reverse();
  // Per-band right edge in hours:
  //   - band i (i < count-1)        → next tier's hoursBefore
  //   - band count-1 (highest tier) → maxHours
  // Per-band left edge = this tier's hoursBefore.
  const bands: Band[] = [];
  for (let i = 0; i < lowToHigh.length; i++) {
    const tier = lowToHigh[i]!;
    const next = lowToHigh[i + 1];
    const leftHours = tier.hoursBefore;
    const rightHours = next ? next.hoursBefore : Math.max(maxHours, leftHours + 1);
    const widthHours = Math.max(0, rightHours - leftHours);
    const tone = toneForTierIndex(lowToHigh.length, i);
    const label =
      i === 0
        ? `< ${lowToHigh[1]?.hoursBefore ?? maxHours}H`
        : !next
          ? `> ${tier.hoursBefore}H`
          : `${tier.hoursBefore}-${next.hoursBefore}H`;
    // Reverse-map low→high index back to policy.tiers index.
    const tierIndex = policy.tiers.length - 1 - i;
    bands.push({
      tierIndex,
      leftHours,
      widthHours,
      refundPercent: tier.refundPercent,
      tone,
      label,
    });
  }
  return bands;
}

/**
 * Build the scale labels printed under the track. For a 3-tier policy that
 * matches the platform default (24/6/0), labels become `0`, `6`, `24`, `48`.
 */
function buildScaleHours(policy: CancelTierTrackPolicy, maxHours: number): readonly number[] {
  const lowToHigh = policy.tiers.slice().reverse();
  const set = new Set<number>([0]);
  for (const t of lowToHigh) set.add(t.hoursBefore);
  set.add(maxHours);
  return Array.from(set).sort((a, b) => a - b);
}

export function CancelTierTrack({
  policy,
  hoursToStart,
  basePrice,
  maxHours = 48,
  locale = 'en-US',
  className,
  ...rest
}: CancelTierTrackProps) {
  const safeHours = Math.max(0, hoursToStart);
  const safeMax = Math.max(maxHours, 1);
  const markerPct = Math.min(1, safeHours / safeMax);

  const bands = buildBands(policy, safeMax);
  const activeTierIndex = matchTierIndex(policy, safeHours);
  const activeTier: CancelTierTrackTier = policy.tiers[activeTierIndex]!;
  const refundAmount = Math.floor((basePrice.amount * activeTier.refundPercent) / 100);

  const totalHoursAcrossBands = bands.reduce((acc, b) => acc + b.widthHours, 0);
  // Defensive fallback if the bands' widths sum to 0 (degenerate policy).
  const safeTotal = totalHoursAcrossBands > 0 ? totalHoursAcrossBands : safeMax;

  const scaleHours = buildScaleHours(policy, safeMax);

  return (
    <div
      data-testid="cancel-tier-track"
      data-active-tier-index={activeTierIndex}
      data-marker-pct={markerPct}
      className={cn('px-1 py-1.5 select-none', className)}
      {...rest}
    >
      {/* Bands */}
      <div
        className="relative h-16 overflow-hidden rounded-[10px] flex"
        role="img"
        aria-label={`Refund tier — ${activeTier.refundPercent}% of price refundable now`}
      >
        {bands.map((band, i) => {
          const flex = band.widthHours / safeTotal;
          const isActive = band.tierIndex === activeTierIndex;
          const tone = TONE_CLASSES[band.tone];
          return (
            <div
              key={`${band.tierIndex}-${i}`}
              data-testid={`cancel-tier-band-${band.tierIndex}`}
              data-tier-index={band.tierIndex}
              data-active={isActive || undefined}
              data-tone={band.tone}
              style={{ flexGrow: flex, flexBasis: 0 }}
              className={cn(
                'relative flex h-full flex-col justify-between px-2.5 py-2 transition-colors',
                isActive ? tone.activeBg : tone.bg,
                isActive && `ring-1 ring-inset ${tone.activeBorder}`,
              )}
            >
              <span
                className={cn(
                  'font-mono text-[9px] font-semibold uppercase tracking-[0.06em]',
                  tone.text,
                )}
              >
                {band.label}
              </span>
              <span
                className={cn(
                  'font-display text-[18px] font-black leading-none tracking-[-0.02em]',
                  tone.text,
                )}
              >
                {band.refundPercent}%
              </span>
            </div>
          );
        })}

        {/* "You are here" marker — vertical line + circle dot */}
        <div
          data-testid="cancel-tier-marker"
          aria-hidden="true"
          className="pointer-events-none absolute -inset-y-0.5 w-0.5 bg-ink shadow-[0_0_0_1px_var(--color-bg)]"
          style={{ insetInlineStart: `calc(${markerPct * 100}% - 1px)` }}
        >
          <div
            className="absolute size-[16px] rounded-full bg-ink border-[3px] border-bg"
            style={{ insetBlockStart: -6, insetInlineStart: -7 }}
          />
        </div>
      </div>

      {/* Hour scale labels */}
      <div className="mt-1.5 flex justify-between">
        {scaleHours.map((h, i) => (
          <span
            key={`${h}-${i}`}
            className="font-mono text-[9px] font-semibold tracking-[0.04em] text-ink-3"
          >
            {h}H
          </span>
        ))}
      </div>

      {/* Side panel: TIME TO START + YOUR REFUND */}
      <div className="mt-3 flex items-end justify-between gap-2">
        <div>
          <span
            className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-ink-3"
            data-testid="cancel-tier-time-label"
          >
            Time to start
          </span>
          <div
            data-testid="cancel-tier-time-value"
            className="font-display text-[26px] font-black leading-none tracking-[-0.025em] text-ink"
          >
            {formatTimeToStart(safeHours)}
          </div>
        </div>
        <div className="text-end">
          <span
            className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-ink-3"
            data-testid="cancel-tier-refund-label"
          >
            Your refund
          </span>
          <div data-testid="cancel-tier-refund-value">
            <CurrencyDisplay
              amount={refundAmount}
              currency={basePrice.currency}
              size="lg"
              locale={locale}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CancelTierTrack;
